import React from 'react';
import {
  BookOpen, CheckCircle2, Trash2, Share2, Eye, MessageSquare, Tag, X, RefreshCw, ClipboardList, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

/**
 * Open a print window for a saved lesson (regular or notes).
 */
function openLessonPrintWindow(lesson) {
  const printWindow = window.open('', '_blank');
  let notesData = null;
  if (lesson.content) {
    try { notesData = JSON.parse(lesson.content); } catch { notesData = null; }
  }
  const isNotes = notesData && notesData.sections;

  if (isNotes) {
    const notes = notesData;
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>${notes.title || lesson.title}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px 30px; color: #1a1a1a; line-height: 1.7; }
        h1 { text-align: center; color: #fff; background: linear-gradient(135deg, #1e3a5f, #1e40af); padding: 18px 24px; border-radius: 12px; font-size: 21px; margin-bottom: 28px; }
        h2 { color: #1e3a5f; border-left: 4px solid #3b82f6; padding-left: 14px; margin-top: 32px; font-size: 17px; }
        .section { margin-bottom: 28px; }
        .content { margin: 10px 0; text-align: justify; font-size: 14px; color: #334155; }
        .key-points { background: linear-gradient(135deg, #eff6ff, #dbeafe); border-radius: 10px; padding: 14px 18px; margin: 14px 0; border-left: 4px solid #3b82f6; }
        .key-points h3 { color: #1e40af; font-size: 13px; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.5px; }
        .key-points ul { margin: 0; padding-left: 18px; }
        .key-points li { margin: 5px 0; font-size: 13px; color: #1e3a5f; }
        .examples { background: linear-gradient(135deg, #f0fdf4, #dcfce7); border-radius: 10px; padding: 14px 18px; margin: 14px 0; border-left: 4px solid #22c55e; }
        .examples h3 { color: #166534; font-size: 13px; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.5px; }
        .diagram { background: #0f172a; border-radius: 12px; padding: 18px 20px; margin: 18px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
        .diagram h3 { color: #38bdf8; font-size: 13px; margin: 0 0 10px 0; text-transform: uppercase; letter-spacing: 1px; }
        .diagram pre { font-family: 'Cascadia Code', 'Fira Code', 'Courier New', monospace; font-size: 11.5px; white-space: pre; overflow-x: auto; background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; border: 1px solid #334155; line-height: 1.5; }
        .vocab-table { width: 100%; border-collapse: separate; border-spacing: 0; margin: 16px 0; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
        .vocab-table th { background: #1e3a5f; color: white; padding: 10px 14px; text-align: left; font-size: 13px; }
        .vocab-table td { padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
        .vocab-table tr:nth-child(even) { background: #f8fafc; }
        .summary { background: linear-gradient(135deg, #fef3c7, #fde68a); border-radius: 10px; padding: 16px 18px; margin: 24px 0; border-left: 4px solid #f59e0b; }
        .summary h3 { color: #92400e; margin: 0 0 8px 0; font-size: 13px; text-transform: uppercase; }
        .review { margin-top: 28px; background: #faf5ff; border-radius: 10px; padding: 16px 18px; border-left: 4px solid #8b5cf6; }
        .review h2 { border: none; padding-left: 0; margin-top: 0; color: #6d28d9; }
        .review ol { padding-left: 20px; }
        .review li { margin: 8px 0; font-size: 14px; color: #4c1d95; }
        .btn { background: #1e40af; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; margin-right: 10px; font-size: 14px; }
        .btn:hover { background: #1e3a8a; }
        .btn-close { background: #e2e8f0; color: #1e293b; }
        .footer { text-align: center; color: #94a3b8; font-size: 11px; margin-top: 36px; border-top: 2px solid #e2e8f0; padding-top: 12px; }
        @media print { .no-print { display: none !important; } body { padding: 20px; } .diagram { background: #1e293b !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .diagram pre { background: #0f172a !important; color: #e2e8f0 !important; } h1 { background: #1e3a5f !important; color: white !important; } }
      </style></head><body>
      <div class="no-print" style="margin-bottom: 20px;">
        <button class="btn" onclick="window.print()">Print</button>
        <button class="btn btn-close" onclick="window.close()">Close</button>
      </div>
      <h1>${notes.title || lesson.title}</h1>
      ${(notes.sections || []).map(s => '<div class="section"><h2>' + (s.heading || '') + '</h2><div class="content">' + (s.content || '') + '</div>' + (s.key_points && s.key_points.length > 0 ? '<div class="key-points"><h3>Key Points</h3><ul>' + s.key_points.map(p => '<li>' + p + '</li>').join('') + '</ul></div>' : '') + (s.examples && s.examples.length > 0 ? '<div class="examples"><h3>Examples</h3><ul>' + s.examples.map(e => '<li>' + e + '</li>').join('') + '</ul></div>' : '') + (s.diagram ? '<div class="diagram"><h3>' + (s.diagram.title || '') + '</h3><pre>' + (s.diagram.description || '') + '</pre></div>' : '') + '</div>').join('')}
      ${notes.vocabulary && notes.vocabulary.length > 0 ? '<h2>Vocabulary</h2><table class="vocab-table"><tr><th>Term</th><th>Definition</th></tr>' + notes.vocabulary.map(v => '<tr><td><strong>' + (v.term || '') + '</strong></td><td>' + (v.definition || '') + '</td></tr>').join('') + '</table>' : ''}
      ${notes.summary ? '<div class="summary"><h3>Summary</h3><p>' + notes.summary + '</p></div>' : ''}
      ${notes.review_questions && notes.review_questions.length > 0 ? '<div class="review"><h2>Review Questions</h2><ol>' + notes.review_questions.map(q => '<li>' + q + '</li>').join('') + '</ol></div>' : ''}
      <div class="footer">Penama Education Portal | ${lesson.created_at ? new Date(lesson.created_at).toLocaleDateString() : 'Recently generated'}</div>
      </body></html>
    `);
  } else {
    const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#f43f5e','#14b8a6','#6366f1','#f97316','#06b6d4','#ec4899'];
    const bgColors = ['#eff6ff','#ecfdf5','#fffbeb','#f5f3ff','#fff1f2','#f0fdfa','#eef2ff','#fff7ed','#ecfeff','#fdf2f8'];
    let contentHtml = '';
    if (lesson.week_lessons && lesson.week_lessons.length > 0) {
      contentHtml += '<table class="scheme"><thead><tr><th>Week</th><th style="width:17%">Topic</th><th style="width:24%">Objectives</th><th style="width:24%">Activities</th><th style="width:13%">Resources</th><th style="width:13%">Assessment</th></tr></thead><tbody>';
      lesson.week_lessons.forEach((week, i) => {
        const objs = week.learning_objectives || week.objectives || [];
        const objList = Array.isArray(objs) ? objs : [objs];
        const acts = week.activities || [];
        const actList = Array.isArray(acts) ? acts : [acts];
        contentHtml += '<tr style="background:' + bgColors[i % bgColors.length] + ';border-left:4px solid ' + colors[i % colors.length] + ';">';
        contentHtml += '<td style="text-align:center;"><span class="week-num" style="background:' + colors[i % colors.length] + ';">' + (week.week || i+1) + '</span></td>';
        contentHtml += '<td class="topic">' + (week.topic || '') + '</td>';
        contentHtml += '<td><ul>' + objList.map(o => '<li>' + o + '</li>').join('') + '</ul></td>';
        contentHtml += '<td><ul>' + actList.map(a => typeof a === 'object' ? '<li><strong>' + (a.name || '') + '</strong>: ' + (a.description || '') + '</li>' : '<li>' + a + '</li>').join('') + '</ul></td>';
        contentHtml += '<td style="color:#64748b;font-size:9.5pt;">' + (week.resources || week.materials || '') + '</td>';
        contentHtml += '<td style="color:#64748b;font-size:9.5pt;">' + (week.assessment || '') + '</td>';
        contentHtml += '</tr>';
      });
      contentHtml += '</tbody></table>';
    } else {
      if (lesson.topic) contentHtml += '<div class="section"><h3>Topic</h3><p>' + lesson.topic + '</p></div>';
      if (lesson.objectives && lesson.objectives.length) contentHtml += '<div class="section"><h3>Objectives</h3><ul>' + lesson.objectives.map(o => '<li>' + o + '</li>').join('') + '</ul></div>';
      if (lesson.introduction) contentHtml += '<div class="section"><h3>Introduction</h3><p>' + lesson.introduction.replace(/\n/g, '<br>') + '</p></div>';
      if (lesson.activities && lesson.activities.length) contentHtml += '<div class="section"><h3>Activities</h3>' + lesson.activities.map(a => '<div style="margin:8px 0;padding:8px;background:#fff;border-radius:4px;"><strong>' + (a.name || a.title || 'Activity') + '</strong><p>' + (a.description || a.details || JSON.stringify(a)) + '</p></div>').join('') + '</div>';
      if (lesson.materials) contentHtml += '<div class="section"><h3>Materials</h3><p>' + lesson.materials.replace(/\n/g, '<br>') + '</p></div>';
      if (lesson.assessment) contentHtml += '<div class="section"><h3>Assessment</h3><p>' + lesson.assessment.replace(/\n/g, '<br>') + '</p></div>';
      if (lesson.homework) contentHtml += '<div class="section"><h3>Homework</h3><p>' + lesson.homework.replace(/\n/g, '<br>') + '</p></div>';
    }

    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>${lesson.title}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; max-width: 1100px; margin: 0 auto; color: #1e293b; }
        .header { background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%); color: white; padding: 24px 28px; border-radius: 12px; margin-bottom: 24px; }
        .header h1 { margin: 0 0 8px 0; font-size: 20pt; }
        .meta { display: flex; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
        .badge { background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 20px; font-size: 12px; color: white; }
        .content { line-height: 1.8; }
        .section { margin: 16px 0; padding: 15px; background: #f9fafb; border-radius: 8px; }
        .section h3 { color: #7c3aed; margin-top: 0; }
        table.scheme { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 10pt; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        table.scheme thead th { background: #1e293b; color: white; padding: 12px 10px; text-align: left; font-weight: 600; }
        table.scheme thead th:first-child { text-align: center; width: 55px; }
        table.scheme td { padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
        table.scheme .week-num { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; color: white; font-weight: 700; font-size: 12px; }
        table.scheme .topic { font-weight: 600; color: #1e293b; }
        table.scheme ul { margin: 2px 0; padding-left: 16px; }
        table.scheme li { margin: 3px 0; color: #475569; }
        .btn { background: #7c3aed; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; margin-right: 10px; font-size: 14px; }
        .btn:hover { background: #6d28d9; }
        .btn-close { background: #e2e8f0; color: #1e293b; }
        .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 9pt; border-top: 1px solid #e2e8f0; padding-top: 12px; }
        @media print { .no-print { display: none !important; } .header, thead th, .week-num, tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>
      <div class="no-print" style="margin-bottom: 20px;">
        <button class="btn" onclick="window.print()">Print</button>
        <button class="btn btn-close" onclick="window.close()">Close</button>
      </div>
      <div class="header">
        <h1>${lesson.title}</h1>
        <div class="meta">
          <span class="badge">${lesson.subject}</span>
          <span class="badge">Year ${lesson.year_level}</span>
          ${lesson.is_week_plan ? '<span class="badge">Week Plan</span>' : ''}
          ${lesson.week_lessons && lesson.week_lessons.length ? '<span class="badge">Scheme of Work</span>' : ''}
        </div>
      </div>
      <div class="content">${contentHtml || 'No content available'}</div>
      <div class="footer">Penama Education Portal | ${lesson.created_at ? new Date(lesson.created_at).toLocaleDateString() : 'Recently generated'}</div>
      </body></html>
    `);
  }
  printWindow.document.close();
}

/**
 * Open a print window for a saved assessment.
 */
function openAssessmentPrintWindow(assessment) {
  const printWindow = window.open('', '_blank');
  const questions = assessment.questions || [];
  printWindow.document.write(`
    <!DOCTYPE html><html><head><title>${assessment.title}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; }
      .header { border-bottom: 3px solid #16a34a; padding-bottom: 15px; margin-bottom: 20px; }
      .header h1 { color: #16a34a; margin: 0; }
      .meta { display: flex; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
      .badge { background: #f3f4f6; padding: 4px 12px; border-radius: 20px; font-size: 12px; }
      .student-info { background: #f9fafb; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
      .question { margin: 20px 0; padding: 15px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #16a34a; }
      .question-num { font-weight: bold; color: #16a34a; }
      .options { margin-top: 10px; padding-left: 20px; }
      .option { margin: 8px 0; padding: 8px 12px; background: white; border: 1px solid #e5e7eb; border-radius: 4px; }
      .answer-line { border-bottom: 1px solid #333; display: inline-block; min-width: 200px; margin-top: 10px; }
      .btn { background: #16a34a; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; margin-right: 10px; }
      .btn:hover { background: #15803d; }
      .btn-outline { background: white; color: #16a34a; border: 2px solid #16a34a; }
      @media print { .no-print { display: none; } }
    </style></head><body>
    <div class="no-print" style="margin-bottom: 20px;">
      <button class="btn" onclick="window.print()">Print Test</button>
      <button class="btn btn-outline" onclick="document.querySelectorAll('.answer').forEach(el => el.style.display = el.style.display === 'none' ? 'block' : 'none')">Toggle Answers</button>
      <button class="btn" style="background:#666;" onclick="window.close()">Close</button>
    </div>
    <div class="header">
      <h1>${assessment.title}</h1>
      <div class="meta">
        <span class="badge">${assessment.subject}</span>
        <span class="badge">Year ${assessment.year_level}</span>
        <span class="badge" style="text-transform:capitalize;">${assessment.assessment_type || 'Quiz'}</span>
        <span class="badge">${assessment.num_questions || questions.length} Questions</span>
        <span class="badge">${assessment.difficulty || 'Medium'}</span>
      </div>
    </div>
    <div class="student-info">
      <strong>Name:</strong> _________________________________ 
      <strong style="margin-left:30px;">Date:</strong> _________________
      <strong style="margin-left:30px;">Score:</strong> _____ / ${assessment.num_questions || questions.length}
    </div>
    ${questions.length > 0 ? questions.map((q, i) => `
      <div class="question">
        <div class="question-num">Question ${i + 1} ${q.marks ? '<span style="float:right;color:#666;font-size:12px;">(' + q.marks + ' marks)</span>' : ''}
          <span style="float:right;color:#999;font-size:11px;margin-right:10px;">${(q.type || '').replace('_', ' ')}</span>
        </div>
        <p>${q.question || q.text || q}</p>
        ${q.type === 'matching' && q.column_a ? `
          <div style="display:flex;gap:40px;margin:10px 0 10px 20px;">
            <div><strong style="color:#1e40af;font-size:13px;">Column A</strong>${q.column_a.map((item, j) => '<div style="margin:6px 0;padding:6px 10px;background:#eff6ff;border-radius:4px;font-size:13px;">' + (j + 1) + '. ' + item + ' ________</div>').join('')}</div>
            <div><strong style="color:#16a34a;font-size:13px;">Column B</strong>${q.column_b.map((item, j) => '<div style="margin:6px 0;padding:6px 10px;background:#f0fdf4;border-radius:4px;font-size:13px;">' + String.fromCharCode(65 + j) + '. ' + item + '</div>').join('')}</div>
          </div>
        ` : q.options ? `
          <div class="options">${q.options.map((opt, j) => `<div class="option">${String.fromCharCode(65 + j)}. ${opt}</div>`).join('')}</div>
        ` : q.type === 'fill_blank' || q.type === 'fill_in_blank' ? '' : '<div class="answer-line"></div>'}
        ${q.answer ? `<p class="answer" style="display:none;color:#16a34a;margin-top:10px;font-weight:bold;">Answer: ${q.answer}</p>` : ''}
      </div>
    `).join('') : `<div class="question"><p>${assessment.content || 'Assessment content will be displayed here.'}</p></div>`}
    ${assessment.answer_key && assessment.answer_key.length > 0 ? `
      <div class="answer" style="display:none;margin-top:30px;padding:20px;background:#f0fdf4;border:2px solid #16a34a;border-radius:8px;">
        <h2 style="color:#16a34a;margin:0 0 15px 0;border-bottom:2px solid #16a34a;padding-bottom:8px;">Answer Key</h2>
        ${assessment.answer_key.map((ans, i) => `
          <div style="margin:8px 0;padding:8px;background:white;border-radius:4px;border-left:3px solid #16a34a;">
            <strong style="color:#16a34a;">Q${ans.question_number || i + 1}:</strong> ${ans.correct_answer || ans.answer || ans}
            ${ans.explanation ? `<br><span style="color:#666;font-size:12px;">Explanation: ${ans.explanation}</span>` : ''}
          </div>
        `).join('')}
      </div>
    ` : ''}
    <div style="margin-top: 30px; text-align: center; color: #666; font-size: 12px;">
      <p>Generated by Penama Education Portal | ${assessment.created_at ? new Date(assessment.created_at).toLocaleDateString() : 'Recently'}</p>
    </div>
    </body></html>
  `);
  printWindow.document.close();
}

export function HistoryTab({
  savedLessons, savedAssessments, loadingSaved,
  selectedTag, setSelectedTag, allTags,
  editingTags, setEditingTags, newTagInput, setNewTagInput,
  updateItemTags, sharedContentIds,
  shareNote, shareAssessment, unshareContent,
  deleteLesson, deleteAssessment,
  viewingSubmissions, setViewingSubmissions,
  submissionsData, setSubmissionsData, loadingSubmissions,
  viewSubmissions, gradeSubmission
}) {
  return (
    <>
      {/* Tag Filter Bar */}
      {allTags.length > 0 && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-sm font-medium text-purple-700">
                <Tag className="h-4 w-4" />
                Filter by tag:
              </div>
              <Badge 
                variant={selectedTag === '' ? 'default' : 'outline'}
                className={`cursor-pointer text-xs ${selectedTag === '' ? 'bg-purple-600' : 'hover:bg-purple-100'}`}
                onClick={() => setSelectedTag('')}
                data-testid="tag-filter-all"
              >
                All
              </Badge>
              {allTags.map(tag => (
                <Badge 
                  key={tag}
                  variant={selectedTag === tag ? 'default' : 'outline'}
                  className={`cursor-pointer text-xs ${selectedTag === tag ? 'bg-purple-600' : 'hover:bg-purple-100'}`}
                  onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                  data-testid={`tag-filter-${tag}`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tag Editing Modal */}
      {editingTags && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-700">Edit Tags</span>
              <Button variant="ghost" size="sm" onClick={() => { setEditingTags(null); setNewTagInput(''); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap mb-2">
              {(editingTags.tags || []).map(tag => (
                <Badge key={tag} className="bg-purple-100 text-purple-700 text-xs gap-1">
                  {tag}
                  <X 
                    className="h-3 w-3 cursor-pointer hover:text-red-500" 
                    onClick={() => {
                      const newTags = editingTags.tags.filter(t => t !== tag);
                      setEditingTags({ ...editingTags, tags: newTags });
                      updateItemTags(editingTags.id, editingTags.type, newTags);
                    }}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newTagInput}
                onChange={e => setNewTagInput(e.target.value)}
                placeholder="Add a tag..."
                className="h-8 text-sm max-w-xs"
                data-testid="new-tag-input"
                onKeyDown={e => {
                  if (e.key === 'Enter' && newTagInput.trim()) {
                    const tag = newTagInput.trim();
                    if (!editingTags.tags.includes(tag)) {
                      const newTags = [...editingTags.tags, tag];
                      setEditingTags({ ...editingTags, tags: newTags });
                      updateItemTags(editingTags.id, editingTags.type, newTags);
                    }
                    setNewTagInput('');
                  }
                }}
              />
              <Button 
                size="sm" 
                variant="outline"
                className="h-8"
                data-testid="add-tag-btn"
                onClick={() => {
                  if (newTagInput.trim()) {
                    const tag = newTagInput.trim();
                    if (!editingTags.tags.includes(tag)) {
                      const newTags = [...editingTags.tags, tag];
                      setEditingTags({ ...editingTags, tags: newTags });
                      updateItemTags(editingTags.id, editingTags.type, newTags);
                    }
                    setNewTagInput('');
                  }
                }}
              >
                Add
              </Button>
              {allTags.filter(t => !editingTags.tags.includes(t)).length > 0 && (
                <div className="flex gap-1 items-center flex-wrap">
                  <span className="text-xs text-muted-foreground ml-2">Quick add:</span>
                  {allTags.filter(t => !editingTags.tags.includes(t)).slice(0, 5).map(tag => (
                    <Badge 
                      key={tag} 
                      variant="outline" 
                      className="cursor-pointer text-xs hover:bg-purple-100"
                      onClick={() => {
                        const newTags = [...editingTags.tags, tag];
                        setEditingTags({ ...editingTags, tags: newTags });
                        updateItemTags(editingTags.id, editingTags.type, newTags);
                      }}
                    >
                      + {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Saved Lessons */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Saved Lessons ({savedLessons.filter(l => !selectedTag || (l.tags || []).includes(selectedTag)).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSaved ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
              </div>
            ) : savedLessons.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No saved lessons yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {savedLessons.filter(l => !selectedTag || (l.tags || []).includes(selectedTag)).map((lesson) => (
                  <div 
                    key={lesson.id} 
                    className="bg-gray-50 p-4 rounded-lg border hover:border-purple-300 hover:bg-purple-50 transition-colors cursor-pointer"
                    onClick={() => openLessonPrintWindow(lesson)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-purple-700 hover:text-purple-900">{lesson.title}</h4>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">{lesson.subject}</Badge>
                          <Badge variant="outline" className="text-xs">Year {lesson.year_level}</Badge>
                          {lesson.is_week_plan && (
                            <Badge className="text-xs bg-purple-100 text-purple-700">Week Plan</Badge>
                          )}
                          {lesson.content && (() => { try { const d = JSON.parse(lesson.content); return d.sections; } catch { return false; } })() && (
                            <Badge className="text-xs bg-blue-100 text-blue-700">Notes</Badge>
                          )}
                          {sharedContentIds[lesson.id] && (
                            <Badge className="text-xs bg-green-100 text-green-700">Shared</Badge>
                          )}
                          {(lesson.tags || []).map(tag => (
                            <Badge key={tag} className="text-xs bg-purple-50 text-purple-600 border-purple-200" variant="outline">{tag}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {lesson.created_at ? new Date(lesson.created_at).toLocaleDateString() : 'Recently saved'}
                        </p>
                        <p className="text-xs text-purple-600 mt-1">Click to view & print</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <a
                          href={`/api/ai-lessons/lesson/${lesson.id}/pptx`}
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium h-8 w-8 text-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                          data-testid={`pptx-lesson-${lesson.id}`}
                          onClick={(e) => e.stopPropagation()}
                          title="Download PowerPoint"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <Button 
                          variant="ghost" size="icon"
                          className="text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                          data-testid={`tag-lesson-${lesson.id}`}
                          onClick={(e) => { e.stopPropagation(); setEditingTags({ id: lesson.id, type: 'lesson', tags: lesson.tags || [] }); setNewTagInput(''); }}
                          title="Manage tags"
                        >
                          <Tag className="h-4 w-4" />
                        </Button>
                        {sharedContentIds[lesson.id] ? (
                          <Button 
                            variant="ghost" size="icon"
                            className="text-green-600 hover:text-green-800 hover:bg-green-50"
                            data-testid={`unshare-lesson-${lesson.id}`}
                            onClick={(e) => { e.stopPropagation(); unshareContent(lesson.id, sharedContentIds[lesson.id]); }}
                            title="Unshare from students"
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" size="icon"
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`share-lesson-${lesson.id}`}
                            onClick={(e) => { e.stopPropagation(); shareNote(lesson.id); }}
                            title="Share with students"
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); deleteLesson(lesson.id); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saved Assessments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Saved Assessments ({savedAssessments.filter(a => !selectedTag || (a.tags || []).includes(selectedTag)).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {savedAssessments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No saved assessments yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {savedAssessments.filter(a => !selectedTag || (a.tags || []).includes(selectedTag)).map((assessment) => (
                  <div 
                    key={assessment.id} 
                    className="bg-gray-50 p-4 rounded-lg border hover:border-green-300 hover:bg-green-50 transition-colors cursor-pointer"
                    onClick={() => openAssessmentPrintWindow(assessment)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-green-700 hover:text-green-900">{assessment.title}</h4>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">{assessment.subject}</Badge>
                          <Badge variant="outline" className="text-xs">Year {assessment.year_level}</Badge>
                          <Badge variant="outline" className="text-xs capitalize">{(assessment.assessment_type || '').replace('_', ' ')}</Badge>
                          <Badge variant="outline" className="text-xs">{assessment.num_questions} Q</Badge>
                          {sharedContentIds[assessment.id] && (
                            <Badge className="text-xs bg-green-100 text-green-700">Shared</Badge>
                          )}
                          {(assessment.tags || []).map(tag => (
                            <Badge key={tag} className="text-xs bg-purple-50 text-purple-600 border-purple-200" variant="outline">{tag}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {assessment.created_at ? new Date(assessment.created_at).toLocaleDateString() : 'Recently saved'}
                        </p>
                        <p className="text-xs text-green-600 mt-1">Click to view & print</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" size="icon"
                          className="text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                          data-testid={`tag-assessment-${assessment.id}`}
                          onClick={(e) => { e.stopPropagation(); setEditingTags({ id: assessment.id, type: 'assessment', tags: assessment.tags || [] }); setNewTagInput(''); }}
                          title="Manage tags"
                        >
                          <Tag className="h-4 w-4" />
                        </Button>
                        {assessment.assessment_type !== 'termly_exam' && (
                          sharedContentIds[assessment.id] ? (
                            <Button 
                              variant="ghost" size="icon"
                              className="text-green-600 hover:text-green-800 hover:bg-green-50"
                              data-testid={`unshare-assessment-${assessment.id}`}
                              onClick={(e) => { e.stopPropagation(); unshareContent(assessment.id, sharedContentIds[assessment.id]); }}
                              title="Unshare from students"
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" size="icon"
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`share-assessment-${assessment.id}`}
                              onClick={(e) => { e.stopPropagation(); shareAssessment(assessment.id); }}
                              title="Share with students"
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                          )
                        )}
                        {sharedContentIds[assessment.id] && (
                          <Button 
                            variant="ghost" size="icon"
                            className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                            data-testid={`view-submissions-${assessment.id}`}
                            onClick={(e) => { e.stopPropagation(); viewSubmissions(sharedContentIds[assessment.id]); }}
                            title="View student submissions"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-assessment-${assessment.id}`}
                          onClick={(e) => { e.stopPropagation(); deleteAssessment(assessment.id); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submissions Review Panel */}
        {viewingSubmissions && submissionsData && (
          <Card className="col-span-full border-orange-200 bg-orange-50/30">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <MessageSquare className="h-5 w-5" />
                  Student Submissions - {submissionsData.shared?.title}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setViewingSubmissions(null); setSubmissionsData(null); }}>Close</Button>
              </div>
              <CardDescription>{submissionsData.submissions?.length || 0} submission(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSubmissions ? (
                <p className="text-center text-muted-foreground py-4">Loading submissions...</p>
              ) : !submissionsData.submissions?.length ? (
                <p className="text-center text-muted-foreground py-8">No submissions yet. Students haven't answered this assessment yet.</p>
              ) : (
                <div className="space-y-4">
                  {submissionsData.submissions.map(sub => (
                    <div key={sub.id} className="bg-white rounded-lg border p-4" data-testid={`submission-card-${sub.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold">{sub.student_name}</h4>
                          <p className="text-xs text-muted-foreground">Submitted: {new Date(sub.submitted_at).toLocaleString()}</p>
                          {sub.time_spent_seconds && (
                            <p className="text-xs text-muted-foreground">Time: {Math.floor(sub.time_spent_seconds / 60)}m {sub.time_spent_seconds % 60}s</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {sub.auto_submitted && (
                            <Badge className="bg-red-100 text-red-700">Auto-submitted</Badge>
                          )}
                          {sub.auto_marks !== null && sub.auto_marks !== undefined && (
                            <Badge className="bg-blue-100 text-blue-700">Auto: {sub.auto_marks}/{sub.auto_total || '?'}</Badge>
                          )}
                          <Badge className={sub.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {sub.status === 'graded' ? `${sub.marks}/${sub.total_marks || '?'}` : 'Pending Review'}
                          </Badge>
                        </div>
                      </div>

                      {/* Anti-cheat flags */}
                      {(sub.anti_cheat_flags?.length > 0 || sub.tab_switch_count > 0 || sub.paste_event_count > 0) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3" data-testid={`anti-cheat-${sub.id}`}>
                          <p className="text-xs font-semibold text-amber-800 mb-1">Integrity Flags:</p>
                          <div className="flex flex-wrap gap-2">
                            {sub.tab_switch_count > 0 && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Tab switches: {sub.tab_switch_count}</span>
                            )}
                            {sub.paste_event_count > 0 && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Paste detected: {sub.paste_event_count}x</span>
                            )}
                            {sub.auto_submitted && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Auto-submitted: {sub.auto_submit_reason}</span>
                            )}
                            {sub.anti_cheat_flags?.map((flag, fi) => (
                              <span key={fi} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{flag}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Student Answers */}
                      <div className="space-y-2 mb-4">
                        {(sub.answers || []).map((ans, qi) => {
                          const question = submissionsData.questions?.[qi];
                          const autoDetail = sub.auto_details?.find(d => d.question === qi + 1);
                          return (
                            <div key={qi} className={`text-sm border-l-2 pl-3 ${autoDetail?.auto_graded ? (autoDetail.is_correct ? 'border-green-400 bg-green-50/50' : 'border-red-300 bg-red-50/50') : 'border-gray-200'} rounded-r p-2`}>
                              <p className="font-medium text-gray-700">Q{qi + 1}: {question?.question || question?.text || `Question ${qi + 1}`}</p>
                              <p className="text-blue-700 mt-1">Answer: {typeof ans === 'object' ? ans.answer : ans}</p>
                              {autoDetail?.auto_graded && (
                                <div className="mt-1 flex items-center gap-2 text-xs">
                                  {autoDetail.is_correct ? (
                                    <span className="text-green-600 font-medium">Correct ({autoDetail.marks_awarded}/{autoDetail.marks_possible})</span>
                                  ) : (
                                    <span className="text-red-500 font-medium">Incorrect (0/{autoDetail.marks_possible}) — Correct: {autoDetail.correct_answer}</span>
                                  )}
                                </div>
                              )}
                              {autoDetail && !autoDetail.auto_graded && (
                                <span className="text-xs text-amber-600 mt-1 block">Manual review needed</span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Grading Form */}
                      {sub.status === 'graded' ? (
                        <div className="bg-green-50 rounded p-3">
                          <p className="text-sm"><strong>Marks:</strong> {sub.marks}/{sub.total_marks || '?'}</p>
                          <p className="text-sm"><strong>Feedback:</strong> {sub.feedback}</p>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded p-3 space-y-2">
                          {sub.auto_marks !== null && sub.auto_marks !== undefined && (
                            <div className="text-sm bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                              <strong className="text-blue-700">Auto-graded:</strong> {sub.auto_marks}/{sub.auto_total || '?'} marks
                              {sub.auto_details && (() => {
                                const manual = sub.auto_details.filter(d => !d.auto_graded).length;
                                return manual > 0 ? <span className="text-amber-600 ml-2">({manual} question{manual > 1 ? 's' : ''} need manual review)</span> : <span className="text-green-600 ml-2">(all auto-graded)</span>;
                              })()}
                            </div>
                          )}
                          <div className="flex gap-3 items-center">
                            <Label className="text-xs whitespace-nowrap">Marks:</Label>
                            <Input 
                              type="number" 
                              className="w-24 h-8 text-sm" 
                              id={`marks-${sub.id}`}
                              defaultValue={sub.auto_marks ?? ''}
                              placeholder={`/ ${submissionsData.total_marks || '?'}`}
                              data-testid={`grade-marks-${sub.id}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Feedback:</Label>
                            <Textarea 
                              className="h-16 text-sm mt-1" 
                              id={`feedback-${sub.id}`}
                              placeholder="Write feedback for the student..."
                              data-testid={`grade-feedback-${sub.id}`}
                            />
                          </div>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`submit-grade-${sub.id}`}
                            onClick={() => {
                              const marks = document.getElementById(`marks-${sub.id}`).value;
                              const feedback = document.getElementById(`feedback-${sub.id}`).value;
                              if (!marks) { return; }
                              if (!feedback) { return; }
                              gradeSubmission(sub.id, marks, feedback);
                            }}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> Submit Grade
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
